import { Chunk } from '@/src/core/streaming/chunk';
import { Tags } from '@/src/core/dicomTags';

function frameCount(chunk: Chunk): number {
  const meta = chunk.metadata;
  if (!meta) return 1;
  const raw = Object.fromEntries(meta)[Tags.NumberOfFrames];
  const frames = parseInt((raw ?? '1').trim(), 10);
  return Number.isFinite(frames) && frames > 0 ? frames : 1;
}

/**
 * Tách những nhóm vừa có file đa-lát vừa có file một lát thành các khối riêng.
 *
 * itk-wasm gom file theo series, còn máy chụp thì hay nhét cả ảnh báo cáo lẫn khối lát cắt vào
 * cùng một series. Nhóm lẫn như vậy làm allocateImageFromChunks ném lỗi và **cả ca** không mở
 * được, kể cả những khối hoàn toàn lành lặn bên cạnh.
 *
 * Đo thật ngày 31/07/2026 tại Mắt Hà Nội – Bắc Ninh: ca OCT của máy OPTOPOL REVO 80 có một series
 * chứa 2 trang báo cáo 3861×2730 nằm chung với 1 khối 128 lát ⇒ "First chunk in a group of chunks
 * (size > 1) is a multi-frame chunk", màn 3D trắng.
 *
 * Mỗi file đa-lát tách thành một khối riêng. Các file một lát còn sót lại trong chính series đó bị
 * BỎ HẲN: đứng cạnh một khối lát cắt thì chúng là trang báo cáo/ảnh dẫn đường máy nhét kèm, không
 * phải dữ liệu 3D. Giữ lại thì VolView dựng chúng thành một "khối" 2–3 lát cỡ 3861×2730 rồi có khi
 * chọn đúng nó làm khối hiển thị — bác sĩ nhận được mấy tờ giấy xếp chồng thay vì võng mạc (đo tại
 * Mắt Hà Nội – Bắc Ninh 31/07/2026: khung 3D ghi "Slice 1366/2730"), kèm mấy dòng "Skipped an
 * unreadable DICOM instance" / "Error loading DICOM data".
 *
 * Chỉ bỏ khi series ĐÓ có khối lát cắt. Series toàn ảnh một lát (CT/MR thường, mỗi lát một file)
 * không đụng tới — đó mới là cách dựng khối bình thường. Ảnh báo cáo vẫn xem được ở chế độ 2D.
 */
export function splitMultiFrameGroups(
  groups: Record<string, Chunk[]>
): Record<string, Chunk[]> {
  const canTach = Object.values(groups).some(
    (chunks) => chunks.length > 1 && chunks.some((chunk) => frameCount(chunk) > 1)
  );
  // Không có gì phải tách thì trả lại đúng đối tượng cũ, không dựng bản sao: nơi gọi vẫn so sánh
  // được bằng tham chiếu, và ca bình thường không tốn thêm một vòng cấp phát nào.
  if (!canTach) return groups;

  const ketQua: Record<string, Chunk[]> = {};

  Object.entries(groups).forEach(([khoa, chunks]) => {
    if (chunks.length < 2) {
      ketQua[khoa] = chunks;
      return;
    }

    const daLat = chunks.filter((chunk) => frameCount(chunk) > 1);
    if (daLat.length === 0) {
      ketQua[khoa] = chunks;
      return;
    }

    daLat.forEach((chunk, i) => {
      ketQua[`${khoa}&multiframe=${i}`] = [chunk];
    });
    // Phần một lát còn lại: bỏ, xem chú thích ở đầu hàm.
  });

  return ketQua;
}
